import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Calendar, Users, Mountain } from "lucide-react";
import { useTranslation } from "react-i18next";
import { HOLIDAY_IMAGES } from "../../data/holidays";
import { MaskedLines, EnquiryButton, EASE } from "./shared";
import { useEarlyBird } from "../../lib/useEarlyBird";

export default function HolidayHero() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const lines = t("holidays.hero.title_lines", { returnObjects: true });
  const eb = useEarlyBird();

  return (
    <section ref={ref} className="relative min-h-[100svh] w-full overflow-hidden bg-forest-deep" data-testid="holidays-hero">
      <motion.div className="absolute inset-0 will-change-transform" style={{ y: imgY, scale: imgScale }}>
        <motion.img
          src={HOLIDAY_IMAGES.hero}
          alt={t("holidays.hero.alt")}
          fetchPriority="high"
          decoding="async"
          width="1600"
          height="739"
          className="w-full h-full object-cover object-[60%_center]"
          initial={{ scale: 1.18, opacity: 0.6 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2.2, ease: EASE }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-forest-deep/70 via-forest-deep/25 to-forest-deep/90" />
      <div className="absolute inset-0 bg-gradient-to-r from-forest-deep/50 to-transparent" />
      <div className="absolute inset-0 grain" />

      <motion.div style={{ opacity: fade }} className="relative z-10 flex flex-col min-h-[100svh] max-w-7xl mx-auto px-6 lg:px-12 pt-28 pb-10 lg:pb-16">
        <div className="flex-1 flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
            className="text-xs uppercase tracking-wider-3 text-bone/80 mb-8 flex items-center gap-3"
          >
            <span className="inline-block w-8 h-px bg-bone/60" />
            {t("holidays.hero.eyebrow")}
          </motion.div>

          <h1 className="font-display text-bone leading-[0.92] tracking-tight uppercase text-[13vw] sm:text-7xl lg:text-[6.5rem] xl:text-[7.5rem]" data-testid="holidays-hero-title">
            <MaskedLines lines={lines} delay={0.35} />
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 0.95 }}
            className="mt-8 md:mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end"
          >
            <div className="lg:col-span-7">
              <p className="font-display italic text-2xl sm:text-3xl md:text-4xl text-bone leading-tight">
                {t("holidays.hero.subheading")}
              </p>
              <p className="mt-3 text-bone/80 text-base sm:text-lg max-w-xl">{t("holidays.hero.supporting")}</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
                <EnquiryButton variant="light" target="packages" testid="hero-cta-packages">
                  {t("holidays.hero.cta_primary")}
                </EnquiryButton>
                <EnquiryButton variant="outlineLight" testid="hero-cta-request">
                  {t("holidays.hero.cta_secondary")}
                </EnquiryButton>
              </div>
            </div>

            <div className="lg:col-span-5 lg:justify-self-end">
              <div className="inline-flex flex-col border border-bone/25 bg-forest-deep/40 backdrop-blur-md px-7 py-6" data-testid="hero-price-highlight">
                <span className="text-[10px] uppercase tracking-wider-3 text-bone/60">{t("holidays.hero.price_label")}</span>
                <span className="font-display text-6xl md:text-7xl text-bone leading-none mt-1">{t("holidays.hero.price")}</span>
                <span className="text-[10px] uppercase tracking-wider-3 text-bone/60 mt-2">{t("holidays.common.per_person")}</span>
                {eb.active && <span className="text-[10px] uppercase tracking-wider-2 text-terracotta mt-3 pt-3 border-t border-bone/15" data-testid="hero-early-bird">{t("holidays.hero.early_bird")}</span>}
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-x-10 gap-y-5 pt-8 mt-10 border-t border-bone/15"
        >
          <Fact icon={<Mountain className="w-4 h-4" />} label={t("holidays.hero.facts.duration")} />
          <Fact icon={<Calendar className="w-4 h-4" />} label={t("holidays.hero.facts.season")} />
          <Fact icon={<Users className="w-4 h-4" />} label={t("holidays.hero.facts.groups")} />
        </motion.div>

        <div className="hidden md:flex absolute bottom-6 right-6 lg:right-12 items-center gap-2 text-bone/70 text-xs uppercase tracking-wider-2">
          <ArrowDown className="w-4 h-4 animate-bounce" />
          {t("holidays.hero.scroll")}
        </div>
      </motion.div>
    </section>
  );
}

function Fact({ icon, label }) {
  return (
    <div className="flex items-center gap-3 text-bone/90">
      <span className="opacity-80">{icon}</span>
      <span className="text-xs uppercase tracking-wider-2">{label}</span>
    </div>
  );
}
